import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ARIA_CARD_ACCENT } from '../../../AriaHandsoff/ariaMessageTypes';
import { ariaCardSurfaceSx } from '../../../AriaHandsoff/ariaHandsoffSurfaces';

const COVER_GRADIENT = 'linear-gradient(135deg, #C87860 0%, #9A5840 100%)';
const COVER_IMAGE_OVERLAY =
  'linear-gradient(135deg, rgba(200,120,96,0.62) 0%, rgba(154,88,64,0.78) 100%)';

/**
 * Shared-event chat card (message.data.messageType = shared_event).
 * Matches member-app SharedEventChatCard layout; partner portal is display-only.
 */
const SharedEventChatCard = ({ event, compact = false }) => {
  if (!event?.eventId) return null;

  const seatsText =
    event.seatsRemaining == null
      ? null
      : `${event.seatsRemaining} seat${event.seatsRemaining === 1 ? '' : 's'} left`;

  const actionLabel = event.isDeleted ? 'Unavailable' : event.isBooked ? 'Booked' : 'Join →';
  const actionMuted = event.isDeleted || event.isBooked;

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: compact ? 280 : 320 },
        maxWidth: '100%',
        borderRadius: '18px',
        overflow: 'hidden',
        ...ariaCardSurfaceSx,
        borderWidth: 1.5,
        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: 68,
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          background: COVER_GRADIENT,
        }}
      >
        {event.coverImageUrl ? (
          <Box
            component="img"
            src={event.coverImageUrl}
            alt=""
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : null}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: event.coverImageUrl ? COVER_IMAGE_OVERLAY : 'transparent',
          }}
        />
        <Typography
          noWrap
          sx={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.8125rem',
            lineHeight: 1.25,
            mr: seatsText ? 1 : 0,
          }}
        >
          {event.title || 'Event'}
        </Typography>
        {seatsText ? (
          <Box
            sx={{
              position: 'relative',
              flexShrink: 0,
              px: 1.125,
              py: 0.375,
              borderRadius: '10px',
              bgcolor: 'rgba(0,0,0,0.25)',
            }}
          >
            <Typography
              sx={{ color: '#fff', fontWeight: 600, fontSize: '0.625rem', lineHeight: 1.3 }}
            >
              {seatsText}
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          px: 1.75,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {event.detailLine ? (
            <Typography
              noWrap
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                color: 'text.primary',
                lineHeight: 1.25,
              }}
            >
              {event.detailLine}
            </Typography>
          ) : null}
          {event.subLine ? (
            <Typography
              noWrap
              sx={{
                fontWeight: 400,
                fontSize: '0.6875rem',
                color: 'text.secondary',
                lineHeight: 1.3,
                mt: event.detailLine ? 0.25 : 0,
              }}
            >
              {event.subLine}
            </Typography>
          ) : null}
        </Box>

        <Button
          size="small"
          variant="contained"
          disabled
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.75rem',
            lineHeight: 1.25,
            borderRadius: '20px',
            px: 1.75,
            py: 0.875,
            minWidth: 0,
            boxShadow: 'none',
            bgcolor: actionMuted ? 'action.hover' : ARIA_CARD_ACCENT,
            color: actionMuted ? 'text.secondary' : 'common.white',
            '&.Mui-disabled': {
              bgcolor: actionMuted ? 'action.hover' : ARIA_CARD_ACCENT,
              color: actionMuted ? 'text.secondary' : 'common.white',
              opacity: actionMuted ? 1 : 0.7,
            },
          }}
        >
          {actionLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default SharedEventChatCard;
